/* eslint-disable @nx/enforce-module-boundaries */
import { Injectable, inject } from '@angular/core';
import { Platform } from '@ionic/angular';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { routerNavigatedAction } from '@ngrx/router-store';
import { Store } from '@ngrx/store';
import { filter, tap } from 'rxjs';

import { SEGMENT_EVENT_METADATA, SegmentContactTraits, SegmentService } from '@ninety/mobile/platform/services';

import { appActions } from '../../../app/app.actions';
import { AppSelectors } from '../../../app/app.selectors';
import { capacitorAppActions } from '../../../capacitor/app/capacitor-app.actions';
import { NinetyPlatform } from '../models/gainsight-global-context';

import { SegmentActions } from './segment.actions';
import { SegmentSelectors } from './segment.selectors';

@Injectable()
export class SegmentEffects {
  private actions$ = inject(Actions);
  private segmentService = inject(SegmentService);
  private readonly store = inject(Store);
  private readonly platform = inject(Platform);

  trackNavigation$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(routerNavigatedAction),
        concatLatestFrom(() => this.store.select(AppSelectors.appReady)),
        filter(([, appReady]) => !!appReady),
        tap(([action]) =>
          this.platform.is('capacitor')
            ? this.segmentService.screen(action.payload.routerState.url)
            : this.segmentService.page()
        )
      ),
    { dispatch: false }
  );

  trackAppState$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(capacitorAppActions.appStateChanged),
        filter(() => this.platform.is('capacitor')),
        tap(({ isActive }) => this.segmentService.track(isActive ? 'App Opened' : 'App Backgrounded'))
      ),
    { dispatch: false }
  );

  initializeUser$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(appActions.appAndCompanyReady),
        concatLatestFrom(() => this.store.select(SegmentSelectors.selectSegmentContactTraits)),
        filter(([, contact]) => !!contact),
        tap(([, contact]) => {
          this.segmentService.identify(contact as SegmentContactTraits);
        })
      ),
    { dispatch: false }
  );

  /**
   * Generic identify effect — merges caller-provided traits with base contact traits.
   * Features dispatch SegmentActions.identify({ traits }) to trigger an identify call
   * with custom data merged on top of the standard contact traits.
   */
  identifyWithTraits$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(SegmentActions.identify),
        concatLatestFrom(() => this.store.select(SegmentSelectors.selectSegmentContactTraits)),
        filter((tuple): tuple is [(typeof tuple)[0], SegmentContactTraits] => !!tuple[1]?.id),
        tap(([{ traits }, contact]) => {
          this.segmentService.identify({
            ...contact,
            ...traits,
          } as SegmentContactTraits);
        })
      ),
    { dispatch: false }
  );

  // TODO: ensure we are triggering on the correct actions i.e. when changing users
  logGroupTraits$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(appActions.appAndCompanyReady),
        concatLatestFrom(() => this.store.select(SegmentSelectors.selectGroupTraits)),
        tap(([, contact]) => this.segmentService.group(contact as SegmentContactTraits))
      ),
    { dispatch: false }
  );

  trackGenericEvent$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(SegmentActions.track),
        concatLatestFrom(() => [
          this.store.select(SegmentSelectors.selectSegmentContactTraits),
          this.store.select(SegmentSelectors.selectGainsightContext),
        ]),
        tap(([{ event, params }, contact, context]) => {
          const payload = {
            ...contact,
            ...context,
            ...params,
            ...(SEGMENT_EVENT_METADATA[event] ?? {}),
          };
          this.segmentService.track(event, payload);
        })
      ),
    { dispatch: false }
  );

  /** For tracking when there is no current user context on pages like login/signup */
  trackAnonymousGenericEvent$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(SegmentActions.trackAnonymousEvent),
        tap(({ event, params }) =>
          this.segmentService.track(event, {
            ...params,
            ...(SEGMENT_EVENT_METADATA[event] ?? {}),
            // Anonymous events skip contact/context, so tag the platform explicitly. Spread last
            // so nothing can override it (authenticated events get it from selectGainsightContext).
            ninetyPlatform: NinetyPlatform.Mobile,
          })
        )
      ),
    { dispatch: false }
  );
}
